import { THRESHOLDS, TIMING, effectiveCvaCutoff } from '@erectus/shared';
import type { Calibration, FrameMetrics, FrameState } from '@erectus/shared';

/**
 * Frame classification and hysteresis (docs/ARCHITECTURE.md §1.5).
 *
 *   forwardHead : CVA below the effective cut-off, or CHA above its limit
 *   slouch      : trunk beyond the forward warn limit OR shoulders protracted
 *   recline     : trunk tilted backwards past the recline limit
 *   good        : everything else
 *   invalid     : the quality gate rejected the frame
 *
 * A raw state only becomes the committed state after it has held for
 * TIMING.hysteresisSec — without that, a single noisy frame would flip the big
 * status indicator and inflate the episode count.
 */

export interface ClassifyInput {
  metrics: FrameMetrics;
  gateOk: boolean;
  calibration?: Calibration | null;
}

export function rawFrameState({ metrics, gateOk, calibration }: ClassifyInput): FrameState {
  if (!gateOk) return 'invalid';

  const trunk = metrics.trunkInclination;
  const prot = metrics.shoulderProtraction;
  const cva = metrics.cva;
  const cha = metrics.cha;

  // Recline is checked first: leaning back also drags CVA down, and calling that
  // "forward head" would be plainly wrong to the user.
  if (trunk !== null && trunk < THRESHOLDS.trunkInclination.reclineBad) return 'recline';

  const slouching =
    (trunk !== null && trunk > THRESHOLDS.trunkInclination.warnMax) ||
    (prot !== null && prot > THRESHOLDS.shoulderProtraction.warn);
  if (slouching) return 'slouch';

  if (cva !== null && cva < effectiveCvaCutoff(calibration?.cvaBaseline ?? null)) return 'forwardHead';
  if (cha !== null && cha > THRESHOLDS.cha.max) return 'forwardHead';

  // Nothing measurable at all — do not credit the user with a good frame.
  if (cva === null && trunk === null && prot === null) return 'invalid';

  return 'good';
}

/**
 * Debounces raw states. `invalid` is committed immediately: bad data must never
 * be smoothed into a good reading.
 */
export class FrameStateMachine {
  private committed: FrameState = 'good';
  private candidate: FrameState | null = null;
  private candidateSince = 0;

  constructor(private readonly hysteresisMs = TIMING.hysteresisSec * 1000) {}

  /** @param tMs milliseconds since session start. */
  push(raw: FrameState, tMs: number): FrameState {
    if (raw === 'invalid' || this.committed === 'invalid') {
      if (raw === 'invalid') {
        this.committed = 'invalid';
        this.candidate = null;
        return this.committed;
      }
      // Leaving `invalid` still needs the hysteresis window below.
    }

    if (raw === this.committed) {
      this.candidate = null;
      return this.committed;
    }

    if (this.candidate !== raw) {
      this.candidate = raw;
      this.candidateSince = tMs;
      return this.committed;
    }

    if (tMs - this.candidateSince >= this.hysteresisMs) {
      this.committed = raw;
      this.candidate = null;
    }
    return this.committed;
  }

  get state(): FrameState {
    return this.committed;
  }

  reset(): void {
    this.committed = 'good';
    this.candidate = null;
    this.candidateSince = 0;
  }
}

export const STATE_LABEL: Record<FrameState, string> = {
  good: 'Good posture',
  forwardHead: 'Head forward',
  slouch: 'Slouching',
  recline: 'Leaning back',
  invalid: 'Not measurable',
};

/** Names from the app icon set (src/ui/icons.tsx); kept as plain strings so scoring stays UI-free. */
export type StateIcon = 'check' | 'headForward' | 'slouch' | 'recline' | 'unknown';

/** Icon + colour token per state. Colour is never the only cue (accessibility). */
export const STATE_STYLE: Record<FrameState, { icon: StateIcon; color: string; soft: string }> = {
  good: { icon: 'check', color: 'var(--color-state-good)', soft: 'var(--color-state-good-soft)' },
  forwardHead: { icon: 'headForward', color: 'var(--color-state-fhp)', soft: 'var(--color-state-fhp-soft)' },
  slouch: { icon: 'slouch', color: 'var(--color-state-slouch)', soft: 'var(--color-state-slouch-soft)' },
  recline: {
    icon: 'recline',
    color: 'var(--color-state-recline)',
    soft: 'var(--color-state-recline-soft)',
  },
  invalid: {
    icon: 'unknown',
    color: 'var(--color-state-invalid)',
    soft: 'var(--color-state-invalid-soft)',
  },
};
